import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import {
  getMyApplications, getReceivedApplications, updateApplication,
  RequirementApplication, ApplicationStatus,
} from "../../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import { device } from "../../../atoms/Utlis/media";
import {
  ClipboardList, CheckCircle, XCircle, Clock, User, Phone,
  MessageSquare, RotateCcw, Inbox
} from "lucide-react";

const gradient = "linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%)";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

import { APP_STATUS_COLOR, APP_STATUS_LABEL, REQ_TYPE_LABEL } from "./marketplaceConstants";

const MarketplaceApplicationsPage: React.FC = () => {
  const [receivedApps, setReceivedApps] = useState<RequirementApplication[]>([]);
  const [loadingReceived, setLoadingReceived] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [ownerNotes, setOwnerNotes] = useState<Record<string, string>>({});
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | "">("");


  const fetchReceived = async () => {
    setLoadingReceived(true);
    try {
      const params = statusFilter ? { status: statusFilter as ApplicationStatus, page_size: 50 } : { page_size: 50 };
      const res = await getReceivedApplications(params);
      setReceivedApps(res.results ?? []);
    } catch { setReceivedApps([]); }
    finally { setLoadingReceived(false); }
  };

  useEffect(() => { fetchReceived(); }, [statusFilter]);

  const handleUpdate = async (id: string, status: ApplicationStatus, notes?: string) => {
    setUpdatingId(id);
    try {
      await updateApplication(id, { status, owner_notes: notes ?? "" });
      showSuccessToast(`Application ${status}!`);
      fetchReceived();
    } catch (err: any) {
      showErrorToast(err?.response?.data?.detail ?? "Failed to update application.");
    } finally {
      setUpdatingId(null);
    }
  };

  const loading = loadingReceived;
  const items = receivedApps;

  return (
    <PageContainer>
      <Header>
        <HeaderLeft>
          <TitleIcon><ClipboardList size={22} /></TitleIcon>
          <div>
            <Title>Applications</Title>
            <Subtitle>Manage recruitment applications</Subtitle>
          </div>
        </HeaderLeft>
      </Header>

        <FilterRow>
          <FilterLabel>Filter:</FilterLabel>
          {(["", "pending", "accepted", "rejected", "withdrawn"] as const).map(s => (
            <FilterChip key={s} $active={statusFilter === s} onClick={() => setStatusFilter(s)}>
              {s === "" ? "All" : APP_STATUS_LABEL[s as ApplicationStatus]}
            </FilterChip>
          ))}
        </FilterRow>

      {loading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <EmptyState>
          <Inbox size={48} strokeWidth={1} color="rgba(23,23,23,0.2)" />
          <EmptyText>
            No applications received yet.
          </EmptyText>
        </EmptyState>
      ) : (
        <CardList>
          {items.map(app => (
            <AppCard key={app.id}>
              <CardTop>
                <CardLeft>
                  {app.requirement_type && (
                    <TypeBadge $type={app.requirement_type}>
                      {REQ_TYPE_LABEL[app.requirement_type] ?? app.requirement_type}
                    </TypeBadge>
                  )}
                  <ReqTitle>{app.requirement_title ?? "Recruitment"}</ReqTitle>
                </CardLeft>
                <StatusBadge $status={app.status}>
                  {APP_STATUS_LABEL[app.status]}
                </StatusBadge>
              </CardTop>

              <CardBody>
                {app.applicant_name && (
                  <InfoRow>
                    <User size={13} />
                    <span>{app.applicant_name}</span>
                    {app.applicant_phone && (
                      <>
                        <Phone size={13} style={{ marginLeft: 8 }} />
                        <a href={`tel:${app.applicant_phone}`} style={{ color: "inherit", textDecoration: "none" }}>
                          {app.applicant_phone}
                        </a>
                        <WaLink
                          href={`https://wa.me/${app.applicant_phone}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          WhatsApp
                        </WaLink>
                      </>
                    )}
                  </InfoRow>
                )}
                {app.message && (
                  <InfoRow>
                    <MessageSquare size={13} />
                    <span style={{ fontStyle: "italic", color: "rgba(23,23,23,0.65)" }}>
                      "{app.message}"
                    </span>
                  </InfoRow>
                )}
                {app.proposed_rate && (
                  <InfoRow>
                    <span style={{ fontSize: 13 }}>₹</span>
                    <span>Proposed: ₹{Number(app.proposed_rate).toLocaleString("en-IN")}</span>
                  </InfoRow>
                )}
                {app.owner_notes && (
                  <InfoRow style={{ color: "#7c4dff" }}>
                    <MessageSquare size={13} />
                    <span>Notes: {app.owner_notes}</span>
                  </InfoRow>
                )}
                <DateLine>
                  <Clock size={11} />
                  Applied {new Date(app.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </DateLine>
              </CardBody>

              {app.status === "pending" && (
                <CardActions>
                  <OwnerNotesInput
                    placeholder="Add notes for applicant (optional)…"
                    value={ownerNotes[app.id] ?? ""}
                    onChange={e => setOwnerNotes(p => ({ ...p, [app.id]: e.target.value }))}
                  />
                  <BtnRow>
                    <ActionBtn
                      $variant="success"
                      disabled={updatingId === app.id}
                      onClick={() => handleUpdate(app.id, "accepted", ownerNotes[app.id])}
                    >
                      <CheckCircle size={13} />
                      {updatingId === app.id ? "Saving…" : "Accept"}
                    </ActionBtn>
                    <ActionBtn
                      $variant="danger"
                      disabled={updatingId === app.id}
                      onClick={() => handleUpdate(app.id, "rejected", ownerNotes[app.id])}
                    >
                      <XCircle size={13} />
                      Reject
                    </ActionBtn>
                  </BtnRow>
                </CardActions>
              )}
            </AppCard>
          ))}
        </CardList>
      )}
    </PageContainer>
  );
};

export default MarketplaceApplicationsPage;

// ─── Styled ───────────────────────────────────────────────────────────────────

const PageContainer = styled.div`
  width: 100%;
  min-height: 100%;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  animation: ${fadeUp} 0.3s ease;
  @media ${device.tablet} { padding: 16px; }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const HeaderLeft = styled.div`display: flex; align-items: center; gap: 12px;`;

const TitleIcon = styled.div`
  width: 44px; height: 44px;
  border-radius: 12px;
  background: ${gradient};
  display: flex; align-items: center; justify-content: center;
  color: white;
`;

const Title = styled.h1`font-size: 20px; font-weight: 800; color: #171717; margin: 0;`;
const Subtitle = styled.p`font-size: 13px; color: rgba(23,23,23,0.5); margin: 0; font-family: Montserrat, sans-serif;`;



const FilterRow = styled.div`display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 16px;`;
const FilterLabel = styled.span`font-family: Montserrat, sans-serif; font-size: 12px; color: rgba(23,23,23,0.5);`;

const FilterChip = styled.button<{ $active: boolean }>`
  padding: 4px 12px;
  border-radius: 100px;
  border: 1px solid ${p => p.$active ? "transparent" : "rgba(23,23,23,0.12)"};
  background: ${p => p.$active ? gradient : "white"};
  color: ${p => p.$active ? "white" : "rgba(23,23,23,0.6)"};
  font-family: Montserrat, sans-serif;
  font-size: 11px; font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
`;

const EmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 60px 0; gap: 12px;
`;

const EmptyText = styled.p`
  font-family: Montserrat, sans-serif;
  font-size: 14px; color: rgba(23,23,23,0.35); text-align: center;
`;

const Spinner = styled.div`
  margin: 60px auto;
  width: 36px; height: 36px;
  border: 3px solid rgba(23,23,23,0.08);
  border-top-color: #c94daa;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const CardList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
`;

const AppCard = styled.div`
  background: white;
  border: 1px solid rgba(23,23,23,0.07);
  border-radius: 14px;
  box-shadow: 0 3px 14px rgba(23,23,23,0.05);
  overflow: hidden;
  animation: ${fadeUp} 0.3s ease;
`;

const CardTop = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 16px 18px 12px; gap: 12px;
`;

const CardLeft = styled.div`flex: 1;`;

const TypeBadge = styled.span<{ $type: string }>`
  display: inline-block; padding: 2px 8px; border-radius: 100px;
  font-size: 10px; font-weight: 700; font-family: Montserrat, sans-serif;
  text-transform: uppercase;
  background: ${p => p.$type === "equipment_requirement" ? "#7c4dff18" : "#c94daa18"};
  color: ${p => p.$type === "equipment_requirement" ? "#7c4dff" : "#c94daa"};
  margin-bottom: 6px;
`;

const ReqTitle = styled.div`font-size: 15px; font-weight: 700; color: #171717;`;

const StatusBadge = styled.span<{ $status: ApplicationStatus }>`
  padding: 4px 12px; border-radius: 100px; flex-shrink: 0;
  font-size: 11px; font-weight: 700; font-family: Montserrat, sans-serif;
  background: ${p => APP_STATUS_COLOR[p.$status]}18;
  color: ${p => APP_STATUS_COLOR[p.$status]};
`;

const CardBody = styled.div`padding: 0 18px 14px; display: flex; flex-direction: column; gap: 7px;`;

const InfoRow = styled.div`
  display: flex; align-items: center; gap: 6px;
  font-family: Montserrat, sans-serif; font-size: 12px; color: rgba(23,23,23,0.65);
`;

const WaLink = styled.a`
  margin-left: 4px; padding: 2px 8px;
  background: #25d366; color: white; border-radius: 100px;
  font-size: 10px; font-weight: 700; text-decoration: none;
  font-family: Montserrat, sans-serif;
`;

const DateLine = styled.div`
  display: flex; align-items: center; gap: 5px;
  font-family: Montserrat, sans-serif; font-size: 11px; color: rgba(23,23,23,0.35);
  margin-top: 4px;
`;

const CardActions = styled.div`
  border-top: 1px solid rgba(23,23,23,0.06);
  padding: 12px 18px; display: flex; flex-direction: column; gap: 10px;
`;

const OwnerNotesInput = styled.input`
  width: 100%; padding: 8px 12px;
  border: 1px solid rgba(23,23,23,0.12); border-radius: 8px;
  font-family: Montserrat, sans-serif; font-size: 13px; color: #171717;
  outline: none; box-sizing: border-box;
  &:focus { border-color: #c94daa; }
`;

const BtnRow = styled.div`display: flex; gap: 8px;`;

const ActionBtn = styled.button<{ $variant: "success" | "danger" }>`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 16px; border-radius: 8px; border: none; cursor: pointer;
  font-family: Urbanist, sans-serif; font-size: 13px; font-weight: 700;
  transition: all 0.2s;
  background: ${p => p.$variant === "success" ? "#16a34a" : "#ef4444"};
  color: white;
  opacity: ${p => (p.disabled ? 0.6 : 1)};
  &:not(:disabled):hover { opacity: 0.88; transform: translateY(-1px); }
`;
